import React, { useState } from 'react';
import { LogOut, Phone, Camera, Trash2, AlertTriangle, CheckCircle, Circle } from 'lucide-react';
import toast from 'react-hot-toast';
import { Avatar } from './Avatar';
import { Modal } from './Modal';
import { Button } from './Button';
import { LegalModal } from './LegalModal';
import { SelfiePrompt } from '../Albums/SelfiePrompt';
import { useAuthStore } from '../../store/authStore';
import { authApi } from '../../api/auth';
import { facePhotoApi } from '../../api/facePhoto';
import styles from './SettingsModal.module.css';

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const SettingsModal: React.FC<SettingsModalProps> = ({ isOpen, onClose }) => {
  const { user, logout, fetchMe, setUser } = useAuthStore();
  const [legalModal, setLegalModal] = useState<'terms' | 'privacy' | null>(null);

  // Phone unlink state
  const [unlinkPhoneConfirm, setUnlinkPhoneConfirm] = useState(false);
  const [unlinkingPhone, setUnlinkingPhone] = useState(false);

  // Face data state
  const [selfiePromptOpen, setSelfiePromptOpen] = useState(false);
  const [deleteFaceConfirm, setDeleteFaceConfirm] = useState(false);
  const [deletingFace, setDeletingFace] = useState(false);

  // Delete account state
  const [deleteAccountExpanded, setDeleteAccountExpanded] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);

  async function handleUnlinkPhone() {
    setUnlinkingPhone(true);
    try {
      await authApi.unlinkPhone();
      await fetchMe();
      setUnlinkPhoneConfirm(false);
      toast.success('Phone number unlinked');
    } catch {
      toast.error('Failed to unlink phone number');
    } finally {
      setUnlinkingPhone(false);
    }
  }

  async function handleDeleteFace() {
    setDeletingFace(true);
    try {
      await facePhotoApi.delete();
      await fetchMe();
      setDeleteFaceConfirm(false);
      toast.success('Face data deleted');
    } catch {
      toast.error('Failed to delete face data');
    } finally {
      setDeletingFace(false);
    }
  }

  async function handleDeleteAccount() {
    setDeletingAccount(true);
    try {
      await authApi.deleteAccount();
      setUser(null);
      window.location.href = '/';
    } catch {
      toast.error('Failed to delete account. Please try again.');
      setDeletingAccount(false);
    }
  }

  function handleClose() {
    setUnlinkPhoneConfirm(false);
    setDeleteFaceConfirm(false);
    setDeleteAccountExpanded(false);
    setDeleteConfirmText('');
    onClose();
  }

  return (
    <>
      <Modal isOpen={isOpen} onClose={handleClose} title="Settings" size="md">
        <div className={styles.body}>

          {/* ── Account ───────────────────────────────────────────── */}
          <div className={styles['account-section']}>
            <p className={styles['section-label']}>Account</p>
            <div className={styles['account-row']}>
              <Avatar src={user?.picture} name={user?.name ?? ''} size="lg" />
              <div>
                <p className={styles['account-name']}>{user?.name}</p>
                <p className={styles['account-email']}>{user?.email}</p>
              </div>
            </div>
          </div>

          {/* ── Phone number ──────────────────────────────────────── */}
          {user?.phone && (
            <div className={styles['info-section']}>
              <p className={styles['section-label']}>Phone number</p>
              {!unlinkPhoneConfirm ? (
                <div className={styles['info-row']}>
                  <div className={styles['info-left']}>
                    <Phone size={13} className={styles['info-icon']} />
                    <span className={styles['info-value']}>{user.phone}</span>
                  </div>
                  <Button variant="danger" size="sm" onClick={() => setUnlinkPhoneConfirm(true)}>
                    Unlink
                  </Button>
                </div>
              ) : (
                <div className={styles['confirm-block']}>
                  <p className={styles['confirm-label']}>
                    You'll need to re-verify your phone to access the app again.
                  </p>
                  <div className={styles['confirm-actions']}>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={handleUnlinkPhone}
                      loading={unlinkingPhone}
                    >
                      Yes, unlink
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setUnlinkPhoneConfirm(false)}
                      disabled={unlinkingPhone}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Face recognition ──────────────────────────────────── */}
          <div className={styles['info-section']}>
            <p className={styles['section-label']}>Face recognition</p>
            {!deleteFaceConfirm ? (
              <div className={styles['face-row']}>
                <div className={styles['info-left']}>
                  {user?.hasFacePhoto ? (
                    <>
                      <CheckCircle size={13} className={styles['icon-green']} />
                      <span className={styles['info-value']}>Selfie saved</span>
                    </>
                  ) : (
                    <>
                      <Circle size={13} className={styles['info-icon']} />
                      <span className={styles['info-value-muted']}>No selfie on file</span>
                    </>
                  )}
                </div>
                <div className={styles['face-actions']}>
                  {user?.hasFacePhoto ? (
                    <>
                      <Button variant="ghost" size="sm" onClick={() => setSelfiePromptOpen(true)}>
                        <Camera size={13} />
                        Update
                      </Button>
                      <Button variant="ghost" size="sm" style={{ color: 'var(--clay-orange)' }} onClick={() => setDeleteFaceConfirm(true)}>
                        Delete
                      </Button>
                    </>
                  ) : (
                    <Button variant="ghost" size="sm" onClick={() => setSelfiePromptOpen(true)}>
                      <Camera size={13} />
                      Add selfie
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              <div className={styles['confirm-block']}>
                <p className={styles['confirm-label']}>
                  Your face data will be permanently removed. You won't appear in face-matched photo results until you add a new selfie.
                </p>
                <div className={styles['confirm-actions']}>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={handleDeleteFace}
                    loading={deletingFace}
                  >
                    Delete face data
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setDeleteFaceConfirm(false)}
                    disabled={deletingFace}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>

          <hr className={styles.divider} />

          {/* ── Sign out ──────────────────────────────────────────── */}
          <div className={styles['sign-out-btn']}>
            <Button variant="secondary" size="sm" onClick={logout}>
              <LogOut className={styles['icon-sm']} />
              Sign out
            </Button>
          </div>

          {/* ── Danger zone ───────────────────────────────────────── */}
          <div className={styles['danger-zone']}>
            <div className={styles['danger-header']}>
              <AlertTriangle size={13} className={styles['danger-icon']} />
              <p className={styles['danger-title']}>Danger zone</p>
            </div>

            {!deleteAccountExpanded ? (
              <Button
                variant="danger"
                size="sm"
                onClick={() => setDeleteAccountExpanded(true)}
              >
                <Trash2 size={13} />
                Delete account
              </Button>
            ) : (
              <div className={styles['delete-account-confirm']}>
                <div className={styles['delete-impact-list']}>
                  <p className={styles['delete-impact-heading']}>This will permanently delete:</p>
                  <ul className={styles['delete-impact-items']}>
                    <li>All events you created and every photo in them</li>
                    <li>All albums, face recognition data, and Rekognition collections</li>
                    <li>Your face selfie and biometric data</li>
                    <li>Your photo likes, album memberships, and profile</li>
                  </ul>
                  <p className={styles['delete-impact-irreversible']}>
                    This cannot be undone. There is no way to recover your data.
                  </p>
                </div>

                <p className={styles['delete-account-prompt']}>
                  Type <strong>DELETE</strong> to confirm
                </p>
                <input
                  className={styles['delete-confirm-input']}
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="DELETE"
                  autoComplete="off"
                  spellCheck={false}
                />
                <div className={styles['confirm-actions']}>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={handleDeleteAccount}
                    disabled={deleteConfirmText !== 'DELETE'}
                    loading={deletingAccount}
                  >
                    <Trash2 size={13} />
                    Delete my account
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setDeleteAccountExpanded(false);
                      setDeleteConfirmText('');
                    }}
                    disabled={deletingAccount}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* ── Legal links ───────────────────────────────────────── */}
          <div className={styles['legal-footer']}>
            <button className={styles['legal-link']} onClick={() => setLegalModal('privacy')}>Privacy Policy</button>
            <span className={styles['legal-sep']}>·</span>
            <button className={styles['legal-link']} onClick={() => setLegalModal('terms')}>Terms of Service</button>
          </div>
        </div>
      </Modal>

      {legalModal && <LegalModal type={legalModal} isOpen onClose={() => setLegalModal(null)} />}

      <SelfiePrompt
        isOpen={selfiePromptOpen}
        onClose={() => setSelfiePromptOpen(false)}
        onCaptured={() => setSelfiePromptOpen(false)}
      />
    </>
  );
};
